import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { DimensionEnums, FormEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { DateRangeFormFieldProps, GenericFormSection } from "models/propTypes/genericFormProps";
import { ReceiptFormTypes, UtilFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { ReceiptViewStatus } from "models/enums/receipt";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
//#endregion
//#region components
import { useReceiptService, useCompanyService } from "serviceHooks";
//#endregion
import { EnumHelper, FilterHelper, LocalizationHelperInstance, QueryStateHelper } from "utils/helpers";
import * as Yup from "yup";
import { CompanyService } from "services";

function useReceiptReportFilterDefinition() {
  const receiptService = useReceiptService();
  const companyService = useCompanyService();
  const [receiptCategoryItems, setReceiptCategoryItems] = React.useState<AutocompleteListItem[]>([]);
  const [receiptPaymentMethodItems, setReceiptPaymentMethodItems] = React.useState<AutocompleteListItem[]>([]);
  const [receiptPaymentMethodTypeItems, setReceiptPaymentMethodTypeItems] = React.useState<AutocompleteListItem[]>([]);

  const receiptCategories = useSelector(
    (x: AppState) => x.auth.selectedCompany.receiptCategories ?? [],
    (left, right) => left?.map((l) => l.id).join("") === right?.map((r) => r.id).join("")
  );
  const receiptPaymentMethods = useSelector(
    (x: AppState) => x.auth.loggedInUser.paymentMethods ?? [],
    (left, right) => left?.map((l) => l.id).join("") === right?.map((r) => r.id).join("")
  );
  const receiptPaymentMethodTypes = useSelector(
    (x: AppState) => x.auth.selectedCompany.usedReceiptPaymentMethodTypes ?? []
  );

  React.useEffect(() => {
    setReceiptCategoryItems(
      receiptCategories.map(
        (receiptCategory): AutocompleteListItem => ({
          id: receiptCategory.id,
          value: receiptCategory.name ?? "",
        })
      )
    );
  }, [receiptCategories]);

  React.useEffect(() => {
    setReceiptPaymentMethodItems(
      receiptPaymentMethods.map(
        (receiptPaymentMethod): AutocompleteListItem => ({
          id: receiptPaymentMethod.id,
          value: receiptPaymentMethod.name ?? "",
        })
      )
    );
    setReceiptPaymentMethodTypeItems(
      receiptPaymentMethodTypes.map(
        (receiptPaymentMethodType): AutocompleteListItem => ({
          id: receiptPaymentMethodType,
          value: LocalizationHelperInstance.getTranslatedEnumValue(
            "Receipt.PaymentMethodType",
            receiptPaymentMethodType
          ),
        })
      )
    );
  }, [receiptPaymentMethods]);

  const receiptStatusIds = EnumHelper.getNumericItemValues(ReceiptViewStatus);
  const receiptStates = receiptStatusIds.map((receiptStatusId) => ({
    id: receiptStatusId,
    value: receiptService.getStateText(receiptStatusId),
  }));

  const searchPersonsAutoComplete = (searchStr: string) => {
    return CompanyService.SearchPersons(searchStr, false).then((result) =>
      result.map(
        (employee): AutocompleteListItem => ({
          id: employee.id,
          value: employee.name ?? "",
          icon: IconDefinitionConstants.WUser,
        })
      )
    );
  };

  const paymentStatusIds = EnumHelper.getNumericItemValues(WintSuperkollAPIModel.PaymentState);
  const paymentStatuses = paymentStatusIds.map((paymentStatusId) => ({
    id: paymentStatusId,
    value: receiptService.getPaymentStatusText(paymentStatusId),
  }));

  const apiToReportFilterFormatter = async (filter: WintSuperkollAPIModel.ReceiptFilter) => {
    const reportFilter: ReceiptFormTypes.Filter = {};

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Receipt.Filter.SerialNumber,
      filter.serialNumberFrom,
      filter.serialNumberTo
    );

    if (filter.states !== undefined) {
      const viewStates = filter.states.map(receiptService.apiToViewStatus);
      reportFilter.viewStatuses = receiptStates.filter((s) => viewStates.includes(s.id as number));
    }

    const receiptCategoryIds =
      filter.receiptCategories !== undefined
        ? filter.receiptCategories
        : filter.receiptCategoryId !== undefined
        ? [filter.receiptCategoryId]
        : undefined;

    if (receiptCategoryIds?.length) {
      reportFilter.receiptCategories = receiptCategoryIds.map((id) => {
        return { id: id, value: receiptCategoryItems.find((i) => i.id === id)?.value ?? "" };
      });
    }

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.Receipt.Filter.Created,
      filter.createdFromDate,
      filter.createdToDate
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Receipt.Filter.Amount,
      filter.amountFrom,
      filter.amountTo
    );

    if (filter.uploadedByUserIds?.length) {
      const uploadedByUsers: Promise<AutocompleteListItem[] | undefined> = companyService
        .getEmployees({ personIds: filter.uploadedByUserIds })
        .then((res) => res.map((person) => ({ id: person.id, value: person.name ?? "" })));
      reportFilter.uploadedByUsers = await uploadedByUsers;
    }

    if (filter.paymentState !== undefined) {
      reportFilter.paymentState = filter.paymentState;
    }

    if (filter.paymentMethods?.length) {
      reportFilter.paymentMethod = filter.paymentMethods[0];
      reportFilter.paymentMethodName = receiptPaymentMethodItems.find(
        (i) => i.id === reportFilter.paymentMethod
      )?.value;
    }

    if (filter.paymentMethodTypes?.length) {
      reportFilter.paymentMethodTypes = filter.paymentMethodTypes.map((paymentMethodType) => {
        return {
          id: paymentMethodType,
          value: receiptPaymentMethodTypeItems.find((i) => i.id === paymentMethodType)?.value ?? "",
        };
      });
    }

    if (filter.dimensions !== undefined) {
      reportFilter.dimensions = filter.dimensions;
    }

    return reportFilter;
  };

  const filterFormFields: GenericFormSection<ReceiptFormTypes.Filter>[] = [
    {
      label: TranslationKeys.Common.Date,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        created: {
          label: TranslationKeys.Common.PurchaseDate,
          type: FormEnums.FormFieldType.DATERANGE,
          extraProps: {
            restrictCrossDateSelection: true,
          } as DateRangeFormFieldProps<ReceiptFormTypes.Filter>,
        },
      },
    },
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        uploadedByUsers: {
          label: TranslationKeys.Common.UploadedBy,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          placeholder: TranslationKeys.Common.Search,
          extraProps: {
            items: (searchStr: string) => searchPersonsAutoComplete(searchStr),
          },
        },
        receiptCategories: {
          label: TranslationKeys.Common.CategoryRefers,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          placeholder: TranslationKeys.Common.Search,
          extraProps: {
            items: receiptCategoryItems,
          },
        },
        serialNumber: {
          label: TranslationKeys.Receipt.ReceiptNumber,
          type: FormEnums.FormFieldType.NUMBERRANGE,
        },
        viewStatuses: {
          label: TranslationKeys.Common.Status,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: receiptStates,
          },
        },
        paymentState: {
          label: TranslationKeys.Receipt.PaymentStatus,
          type: FormEnums.FormFieldType.SELECTION,
          extraProps: {
            items: paymentStatuses,
          },
        },
        paymentMethodTypes: {
          label: TranslationKeys.Receipt.PaymentMethodType,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          placeholder: TranslationKeys.Common.Search,
          extraProps: {
            items: receiptPaymentMethodTypeItems,
          },
        },
      },
    },
    {
      label: TranslationKeys.Common.Amount,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        amount: {
          label: TranslationKeys.Common.TotalAmount,
          type: FormEnums.FormFieldType.NUMBERRANGE,
        },
      },
    },
    {
      label: TranslationKeys.Common.Tags,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        dimensions: {
          label: TranslationKeys.Common.Tags,
          type: FormEnums.FormFieldType.DIMENSIONMULTIPLEINPUTSELECTOR,
          extraProps: {
            type: DimensionEnums.DimensionCategory.Receipt,
          },
        },
      },
    },
  ];

  const validationSchema = Yup.object<ReceiptFormTypes.Filter>({
    amount: Yup.object<UtilFormTypes.NumberRange>({
      lowest: Yup.number(),
      highest: Yup.number(),
    }).validateNumberRange(),
    serialNumber: Yup.object<UtilFormTypes.NumberRange>({
      lowest: Yup.number(),
      highest: Yup.number(),
    }).validateNumberRange(),
  });

  const filterQueryStateHandler: FilterQueryStateHandler<ReceiptFormTypes.Filter> = {
    serialNumber: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Receipt.Filter.SerialNumber),
    viewStatuses: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.Receipt.Filter.ViewStatuses,
      receiptStates
    ),
    receiptCategories: QueryStateHelper.multipleAutoCompleteItemsQueryEncoder(
      FormFieldConstants.Receipt.Filter.ReceiptCategories
    ),
    created: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.Receipt.Filter.Created),
    amount: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Receipt.Filter.Amount),
    uploadedByUsers: QueryStateHelper.multipleAutoCompleteItemsQueryEncoder(
      FormFieldConstants.Receipt.Filter.UploadedByUsers
    ),
    paymentState: QueryStateHelper.numberQueryEncoder(FormFieldConstants.Receipt.Filter.PaymentState),
    paymentMethod: QueryStateHelper.numberQueryEncoder(FormFieldConstants.Receipt.Filter.PaymentMethod),
    paymentMethodName: QueryStateHelper.stringQueryEncoder(FormFieldConstants.Receipt.Filter.PaymentMethodName),
    paymentMethodTypes: QueryStateHelper.multipleAutoCompleteItemsQueryEncoder(
      FormFieldConstants.Receipt.Filter.PaymentMethodTypes
    ),
    dimensions: QueryStateHelper.dimensionsQueryEncoder(FormFieldConstants.Receipt.Filter.Dimensions),
  };

  return { filterFormFields, filterQueryStateHandler, apiToReportFilterFormatter, validationSchema };
}

export default useReceiptReportFilterDefinition;
